import { Link } from 'react-router';
import { ThemeToggle } from '@mieweb/ui';
import { motion } from 'motion/react';
import { BrandLockup } from '../components/BrandMark';
import { PageMeta } from '../components/PageMeta';
import { StructuredData } from '../components/StructuredData';
import { competitors } from '../data/competitors';
import { useMemo } from 'react';

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };

export function ComparePage() {
  const webPageData = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Pollenate Alternatives & Comparisons',
    description: 'Compare Pollenate with popular feedback, survey, and experience management tools. See pricing, features, and why teams choose Pollenate.',
    url: 'https://pollenate.dev/vs',
  }), []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 dark:from-neutral-950 to-white dark:to-neutral-900">
      {/* SEO */}
      <PageMeta
        title="Pollenate Comparisons — See How We Stack Up"
        description="Compare Pollenate with Qualtrics, Google Forms, Typeform, SurveyMonkey, Hotjar, Canny, and more. See pricing, features, and why teams switch."
        url="https://pollenate.dev/vs"
      />
      <StructuredData id="compare-index" data={webPageData} />

      {/* Navigation */}
      <nav className="bg-white dark:bg-neutral-900 border-b border-slate-200 dark:border-neutral-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <BrandLockup
                iconClassName="h-7 w-7"
                textClassName="text-xl font-bold text-slate-900 dark:text-white"
              />
            </Link>
            <div className="flex items-center gap-3">
              <ThemeToggle mode="two-way" size="sm" variant="ghost" />
              <Link
                to="/docs"
                className="text-slate-600 dark:text-neutral-400 hover:text-slate-900 dark:hover:text-white transition-colors text-sm"
              >
                Docs
              </Link>
              <Link
                to="/login"
                className="text-slate-600 dark:text-neutral-400 hover:text-slate-900 dark:hover:text-white transition-colors text-sm"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ duration: 0.5 }}
            className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-6"
          >
            See How{' '}
            <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">
              Pollenate
            </span>{' '}
            Compares
          </motion.h1>
          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-lg sm:text-xl text-slate-600 dark:text-neutral-400 max-w-2xl mx-auto"
          >
            Honest, detailed comparisons with the most popular feedback, survey, and experience management tools.
          </motion.p>
        </div>
      </header>

      {/* Competitor grid */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {competitors.map((competitor, i) => (
            <motion.div
              key={competitor.slug}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15 + i * 0.08 }}
            >
              <Link
                to={`/vs/${competitor.slug}`}
                className="group block bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-xl p-6 hover:border-amber-300 dark:hover:border-amber-700/50 hover:shadow-lg hover:shadow-amber-100/30 dark:hover:shadow-amber-900/10 transition-all"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl">{competitor.logoEmoji}</span>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors">
                      vs {competitor.name}
                    </h2>
                    <p className="text-xs text-slate-500 dark:text-neutral-500">{competitor.category}</p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 dark:text-neutral-400 mb-4 line-clamp-2">
                  {competitor.tagline}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-400 dark:text-neutral-500">
                    {competitor.pricing.hasFreeTier ? 'Has free tier' : 'No free tier'} &bull; Est. {competitor.yearFounded}
                  </span>
                  <span className="text-amber-500 group-hover:translate-x-1 transition-transform text-sm">→</span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-neutral-800 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-400 dark:text-neutral-500">
            &copy; 2026 Pollenate by{' '}
            <a
              href="https://bluehive.com?utm_source=pollenate&utm_medium=website&utm_campaign=footer_copyright"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-600 dark:hover:text-neutral-300 transition-colors"
            >
              BlueHive Health
            </a>
            . All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/docs" className="text-slate-400 dark:text-neutral-500 hover:text-slate-600 dark:hover:text-neutral-300 transition-colors">
              Docs
            </Link>
            <Link to="/privacy" className="text-slate-400 dark:text-neutral-500 hover:text-slate-600 dark:hover:text-neutral-300 transition-colors">
              Privacy
            </Link>
            <Link to="/terms" className="text-slate-400 dark:text-neutral-500 hover:text-slate-600 dark:hover:text-neutral-300 transition-colors">
              Terms
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
